import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import clsx from 'clsx';
import { useEscapeLayer } from '../../components/ui';

export interface MenuEntry {
  /** stable key */
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  /** a link rather than an action: downloads and the like */
  href?: string;
  title?: string;
  danger?: boolean;
  /** shown but not usable, e.g. Undo with nothing to undo */
  disabled?: boolean;
  onSelect?: () => void;
}

/**
 * A button that opens a short list of actions under it: the top bar's ⋯ and Download
 * menus, a row's ⋯. A plain list of buttons with `role="menu"`; arrow keys move between
 * them, Escape and a click elsewhere close it, and focus goes back to the button.
 */
export function Menu({
  label,
  trigger,
  entries,
  align = 'right',
  className,
  triggerClassName = 'btn btn-sm btn-ghost',
  width = 'w-52',
  testId,
}: {
  /** what the button is called for assistive technology and the tooltip */
  label: string;
  trigger: ReactNode;
  entries: (MenuEntry | false | null | undefined)[];
  align?: 'left' | 'right';
  className?: string;
  triggerClassName?: string;
  width?: string;
  testId?: string;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const list = entries.filter((e): e is MenuEntry => Boolean(e));
  const close = (refocus = true) => {
    setOpen(false);
    if (refocus) button.current?.focus();
  };
  useEscapeLayer(open, () => close());
  /*
   * Kept inside the screen and inside whatever scrolls around it. A menu hangs off its
   * button at a fixed width, so the ＋ between two rows on a phone opened one that ran
   * past the right edge: the item list scrolled sideways and the first entry was cut off.
   * Measured once as it opens, before it is painted and before its first entry takes the
   * focus (which would scroll the list sideways to it), and moved sideways as needed.
   */
  const panel = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    const el = panel.current;
    if (!open || !el) return;
    const box = el.getBoundingClientRect();
    let lo = 8;
    let hi = window.innerWidth - 8;
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      if (getComputedStyle(p).overflowX === 'visible') continue;
      const clip = p.getBoundingClientRect();
      lo = Math.max(lo, clip.left + p.clientLeft + 4);
      hi = Math.min(hi, clip.left + p.clientLeft + p.clientWidth - 4);
      break;
    }
    let dx = 0;
    if (box.right > hi) dx = hi - box.right;
    if (box.left + dx < lo) dx = lo - box.left;
    // straight on the element: it is created afresh each time the menu opens
    if (dx) el.style.transform = `translateX(${Math.round(dx)}px)`;
    /*
     * Upwards when it does not fit below: the ⋯ of the last row on a sheet opened a menu
     * whose last entries — "Remove from sheet" among them — lay under the bottom of the
     * window. Only when there is more room above the button than below it.
     */
    let top = 8;
    let bottom = window.innerHeight - 8;
    for (let p = el.parentElement; p && p !== document.body; p = p.parentElement) {
      if (getComputedStyle(p).overflowY === 'visible') continue;
      const clip = p.getBoundingClientRect();
      top = Math.max(top, clip.top + p.clientTop + 4);
      bottom = Math.min(bottom, clip.top + p.clientTop + p.clientHeight - 4);
      break;
    }
    const anchor = button.current?.getBoundingClientRect();
    if (anchor && box.bottom > bottom && anchor.top - top > bottom - anchor.bottom) {
      el.style.top = 'auto';
      el.style.bottom = '100%';
      el.style.marginTop = '0';
      el.style.marginBottom = '4px';
    }
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const away = (e: MouseEvent) => {
      if (!ref.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', away);
    // the first entry takes the focus, so the arrows work straight away
    ref.current?.querySelector<HTMLElement>('[role=menuitem]:not(:disabled)')?.focus();
    return () => document.removeEventListener('mousedown', away);
  }, [open]);

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp' && e.key !== 'Home' && e.key !== 'End') return;
    const items = [...(ref.current?.querySelectorAll<HTMLElement>('[role=menuitem]:not(:disabled)') ?? [])];
    if (!items.length) return;
    e.preventDefault();
    const at = items.indexOf(document.activeElement as HTMLElement);
    const next =
      e.key === 'Home' ? 0
      : e.key === 'End' ? items.length - 1
      : e.key === 'ArrowDown' ? (at + 1) % items.length
      : (at - 1 + items.length) % items.length;
    items[next]!.focus();
  };

  const itemClass = (entry: MenuEntry) =>
    clsx(
      'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm hover:bg-[var(--surface-muted)] focus:bg-[var(--surface-muted)] focus:outline-none disabled:cursor-not-allowed disabled:opacity-50',
      entry.danger && 'text-[var(--color-danger-500)]',
    );

  return (
    <div ref={ref} className={clsx('relative', className)} onKeyDown={onKeyDown}>
      <button
        ref={button}
        type="button"
        className={triggerClassName}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        title={label}
        data-testid={testId}
        onClick={() => setOpen((o) => !o)}
      >
        {trigger}
      </button>
      {open && (
        <div
          ref={panel}
          role="menu"
          aria-label={label}
          className={clsx(
            'card absolute top-full z-40 mt-1 max-w-[calc(100vw-16px)] p-1 shadow-lg',
            align === 'right' ? 'right-0' : 'left-0',
            width,
          )}
        >
          {list.map((entry) =>
            entry.href ? (
              <a
                key={entry.id}
                role="menuitem"
                href={entry.href}
                title={entry.title}
                className={itemClass(entry)}
                onClick={() => close(false)}
              >
                {entry.icon}
                {entry.label}
              </a>
            ) : (
              <button
                key={entry.id}
                type="button"
                role="menuitem"
                title={entry.title}
                disabled={entry.disabled}
                className={itemClass(entry)}
                onClick={() => {
                  // focus back on the button first, so a drawer this opens returns there
                  close();
                  entry.onSelect?.();
                }}
              >
                {entry.icon}
                {entry.label}
              </button>
            ),
          )}
        </div>
      )}
    </div>
  );
}
